import { ReactNode, UIEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { com, Nullable } from '@eidu/form'
import { AssetTypeDefinition, findAssetTypeByLabel, getSupportedReferenceTypeIdsForAssetType } from './assetTypes'
import { AssetEntityTypeConfig, ADMIN_ASSET_TYPES_NAME } from './useAssetTypeRegistry'
import { AssetReferenceMatch } from '../../components/misc/assetReferences'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import useEntityRepository from '../../io/useEntityRepository'
import useEntitiesOfType from '../../io/useEntitiesOfType'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import useEntityById from '../../io/useEntityById'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import type DraftFieldValue from '../../domain/entity/DraftFieldValue'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import useDebounced from '../../ui/debounce/useDebounced'
import { useSnackBar } from '../../components/feedback/SnackBarProvider'
import createEntityToCreate from '../entity/createEntityToCreate'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { isOrderField } from '../../util/entityType/orderField'
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull

const assetImageSize = 72

const UPLOAD_EXTENSION_MAP: Record<string, string[]> = {
  jpg: ['.jpg', '.jpeg'],
  png: ['.png'],
  svg: ['.svg'],
  mp3: ['.mp3'],
  mp4: ['.mp4'],
}

const SUPPORTED_UPLOAD_TYPE_IDS = new Set(Object.keys(UPLOAD_EXTENSION_MAP))

const normalizeUploadTypeId = (file: File): string | undefined => {
  const name = file.name ?? ''
  const ext = name.includes('.') ? name.split('.').pop()?.toLowerCase() ?? '' : ''
  const normalizedExt = ext === 'jpeg' ? 'jpg' : ext
  if (SUPPORTED_UPLOAD_TYPE_IDS.has(normalizedExt)) return normalizedExt

  const mime = (file.type ?? '').toLowerCase()
  if (mime === 'image/png') return 'png'
  if (mime === 'image/jpeg') return 'jpg'
  if (mime === 'image/svg+xml') return 'svg'
  if (mime === 'audio/mpeg') return 'mp3'
  if (mime === 'video/mp4') return 'mp4'
  return undefined
}

const buildUploadAccept = (types: AssetTypeDefinition[]) => {
  const extensions = types.flatMap((type) => UPLOAD_EXTENSION_MAP[type.id] ?? [])
  return Array.from(new Set(extensions)).join(',')
}

const getFileBaseName = (fileName: string) => {
  const trimmed = fileName.trim()
  const withoutExt = trimmed.replace(/\.[^.]+$/, '')
  return withoutExt.length > 0 ? withoutExt : trimmed
}

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const base64 = result.includes(',') ? result.split(',')[1] ?? '' : ''
      if (!base64) {
        reject(new Error('Failed to read file payload.'))
        return
      }
      resolve(base64)
    }
    reader.onerror = () => {
      reject(reader.error ?? new Error('Failed to read file.'))
    }
    reader.readAsDataURL(file)
  })

type AssetFieldIds = {
  payload?: FieldId
  type?: FieldId
  thumbnail?: FieldId
}

type AssetMediaMeta = {
  mediaType: 'image' | 'audio' | 'video'
  mimeType: string
}

type AssetMediaState = {
  src: string
  label: string
  mediaType: AssetMediaMeta['mediaType']
}

const getFieldString = (values: ReadonlyMap<FieldId, Nullable<FieldValue>>, fieldId?: FieldId) =>
  fieldId ? fieldValueToString(values.get(fieldId) ?? null).trim() : ''

const normalizeFieldName = (value: string) => value.trim().toLowerCase()
const normalizeValue = (value: string) => value.trim().toLowerCase()

const findFieldById = (entityType: EntityType, fieldId?: FieldId) =>
  fieldId ? entityType.fields.asJsReadonlyArrayView().find((field) => field.id.equals(fieldId)) : undefined

const findFieldByName = (entityType: EntityType, name: string) =>
  entityType.fields.asJsReadonlyArrayView().find((field) => normalizeFieldName(field.name) === normalizeFieldName(name))

const findEntityTypeByName = (
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>,
  targetName: string
): EntityType | undefined => {
  const normalizedTarget = normalizeValue(targetName)
  const types = Array.from(entityTypes.values())
  const exactMatch = types.find((type) => normalizeValue(type.name) === normalizedTarget)
  if (exactMatch) return exactMatch
  return types.find((type) => normalizeValue(getTypeDisplayName(type.name)) === normalizedTarget)
}

const getAssetTypeLabel = (asset: EntityWithLabelAndRelated, fieldIds: AssetFieldIds) => {
  if (!fieldIds.type) return undefined
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const typeValue = values.get(fieldIds.type) ?? null
  if (typeValue instanceof ReferenceValue) {
    const referenced = asset.getReferencedEntity(typeValue.value)
    return referenced?.primaryLabelText?.trim()
  }
  return typeValue ? fieldValueToString(typeValue).trim() : undefined
}

const getAssetFieldIds = (assetConfig?: AssetEntityTypeConfig): AssetFieldIds => ({
  payload: assetConfig?.payloadFieldId,
  type: assetConfig?.typeFieldId,
  thumbnail: assetConfig?.thumbnailFieldId,
})

const resolveAssetTypeForEntity = (
  asset: EntityWithLabelAndRelated,
  assetConfig?: AssetEntityTypeConfig
): AssetTypeDefinition | undefined => {
  if (!assetConfig) return undefined
  if (assetConfig.allowedTypes.length === 1 && !assetConfig.typeFieldId) return assetConfig.allowedTypes[0]

  const typeLabel = getAssetTypeLabel(asset, getAssetFieldIds(assetConfig))
  if (!typeLabel) return assetConfig.allowedTypes.length === 1 ? assetConfig.allowedTypes[0] : undefined

  const resolved = findAssetTypeByLabel(typeLabel)
  if (!resolved) return undefined
  return assetConfig.allowedTypeIds.includes(resolved.id) ? resolved : undefined
}

export const resolveInitialCollectionEntityTypeId = ({
  orderedCollections,
  currentLinkedAssetTypeId,
  supportedReferenceTypeIds,
}: {
  orderedCollections: AssetEntityTypeConfig[]
  currentLinkedAssetTypeId?: string
  supportedReferenceTypeIds: string[]
}): string | null => {
  if (currentLinkedAssetTypeId) {
    const matchingCollection = orderedCollections.find((config) =>
      config.allowedTypeIds.some((allowed) => normalizeValue(allowed) === normalizeValue(currentLinkedAssetTypeId))
    )
    if (matchingCollection) {
      return matchingCollection.entityTypeId.toString()
    }
  }

  for (const typeId of supportedReferenceTypeIds) {
    const match = orderedCollections.find((config) =>
      config.allowedTypeIds.some((allowed) => normalizeValue(allowed) === normalizeValue(typeId))
    )
    if (match) {
      return match.entityTypeId.toString()
    }
  }

  return orderedCollections[0]?.entityTypeId.toString() ?? null
}

export const resolveNextCollectionEntityTypeId = ({
  orderedCollections,
  currentLinkedAssetTypeId,
  supportedReferenceTypeIds,
  selectedEntityTypeId,
  hasUserSelectedCollection,
}: {
  orderedCollections: AssetEntityTypeConfig[]
  currentLinkedAssetTypeId?: string
  supportedReferenceTypeIds: string[]
  selectedEntityTypeId: string | null
  hasUserSelectedCollection: boolean
}): string | null => {
  const hasValidSelection =
    !!selectedEntityTypeId &&
    orderedCollections.some((config) => config.entityTypeId.toString() === selectedEntityTypeId)

  if (hasUserSelectedCollection && hasValidSelection) {
    return selectedEntityTypeId
  }

  const preferredSelection = resolveInitialCollectionEntityTypeId({
    orderedCollections,
    currentLinkedAssetTypeId,
    supportedReferenceTypeIds,
  })

  if (currentLinkedAssetTypeId && preferredSelection) {
    return preferredSelection
  }

  if (hasValidSelection) {
    return selectedEntityTypeId
  }

  return preferredSelection
}

const getAssetMediaMeta = (assetType?: AssetTypeDefinition): AssetMediaMeta | undefined => {
  if (!assetType?.mediaType || !assetType.mimeType) return undefined
  return { mediaType: assetType.mediaType, mimeType: assetType.mimeType }
}

const getAssetThumbnailSource = (
  asset: EntityWithLabelAndRelated,
  fieldIds: AssetFieldIds,
  assetType?: AssetTypeDefinition
) => {
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const thumbnail = getFieldString(values, fieldIds.thumbnail)
  if (thumbnail.length) return `data:image/png;base64,${thumbnail}`
  const mediaMeta = getAssetMediaMeta(assetType)
  if (!mediaMeta || mediaMeta.mediaType !== 'image') return assetType?.defaultThumbnailSrc
  const payload = getFieldString(values, fieldIds.payload)
  if (payload.length) return `data:${mediaMeta.mimeType};base64,${payload}`
  return assetType?.defaultThumbnailSrc
}

const getAssetPayloadSource = (
  asset: EntityWithLabelAndRelated,
  fieldIds: AssetFieldIds,
  assetType?: AssetTypeDefinition
): { src: string; mediaType: AssetMediaMeta['mediaType'] } | undefined => {
  const mediaMeta = getAssetMediaMeta(assetType)
  if (!mediaMeta) return undefined
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const payload = getFieldString(values, fieldIds.payload)
  if (payload.length) {
    return { src: `data:${mediaMeta.mimeType};base64,${payload}`, mediaType: mediaMeta.mediaType }
  }
  if (mediaMeta.mediaType !== 'image') return undefined
  const thumbnail = getFieldString(values, fieldIds.thumbnail)
  return thumbnail.length ? { src: `data:image/png;base64,${thumbnail}`, mediaType: 'image' } : undefined
}

const AssetImage = ({
  src,
  alt,
  onClick,
  showPlay,
}: {
  src?: string
  alt?: string
  onClick?: () => void
  showPlay?: boolean
}) => (
  <Box
    sx={{
      width: assetImageSize,
      height: assetImageSize,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      backgroundColor: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0,
      position: 'relative',
      cursor: onClick ? 'pointer' : 'default',
    }}
    onClick={onClick}
  >
    {src ? (
      <Box
        component="img"
        src={src}
        alt={alt ?? 'Asset media'}
        loading="lazy"
        sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    ) : (
      <Typography variant="caption" color="text.secondary">
        No image
      </Typography>
    )}
    {showPlay && (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlayArrowIcon fontSize="small" />
        </Box>
      </Box>
    )}
  </Box>
)

type AssetRowProps = {
  asset?: EntityWithLabelAndRelated | null
  assetConfig?: AssetEntityTypeConfig
  fieldIds: AssetFieldIds
  fallbackPrimary: string
  fallbackSecondary?: string
  onDoubleClick?: () => void
  onShowMedia?: (media: AssetMediaState) => void
}

const AssetRow = ({
  asset,
  assetConfig,
  fieldIds,
  fallbackPrimary,
  fallbackSecondary,
  onDoubleClick,
  onShowMedia,
}: AssetRowProps) => {
  const primaryLabel = asset?.primaryLabelText ?? fallbackPrimary
  const secondaryLabel = asset?.secondaryLabelText ?? fallbackSecondary
  const resolvedAssetType = asset ? resolveAssetTypeForEntity(asset, assetConfig) : undefined
  const mediaMeta = getAssetMediaMeta(resolvedAssetType)
  const imageSource = asset ? getAssetThumbnailSource(asset, fieldIds, resolvedAssetType) : undefined
  const payloadMedia = asset ? getAssetPayloadSource(asset, fieldIds, resolvedAssetType) : undefined
  const handleShowMedia = payloadMedia ? () => onShowMedia?.({ ...payloadMedia, label: primaryLabel }) : undefined
  const showPlay = mediaMeta?.mediaType === 'audio' || mediaMeta?.mediaType === 'video'

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      onDoubleClick={onDoubleClick}
      sx={{
        py: 1,
        cursor: onDoubleClick ? 'pointer' : 'default',
        borderRadius: 1,
        px: 1,
        '&:hover': onDoubleClick
          ? {
              backgroundColor: 'action.hover',
            }
          : undefined,
      }}
    >
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" color="primary" noWrap>
          {primaryLabel}
        </Typography>
        {secondaryLabel && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {secondaryLabel}
          </Typography>
        )}
      </Stack>
      <AssetImage src={imageSource} alt={primaryLabel} onClick={handleShowMedia} showPlay={showPlay} />
    </Stack>
  )
}

type AssetReferenceEditorDialogContentProps = {
  assetReference: AssetReferenceMatch
  assetEntityTypes: AssetEntityTypeConfig[]
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  currentAssetType?: AssetTypeDefinition
  onSelectAsset: (assetId: string) => void
}

const AssetReferenceEditorDialogContent = ({
  assetReference,
  assetEntityTypes,
  entityTypes,
  currentAssetType,
  onSelectAsset,
}: AssetReferenceEditorDialogContentProps) => {
  const { showSnackBar } = useSnackBar()
  const currentAssetId = useMemo(
    () => entityIdFromStringOrNull(assetReference.uuid) ?? undefined,
    [assetReference.uuid]
  )
  const { entity: currentAsset, loading: currentAssetLoading } = useEntityById(currentAssetId)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchQuery] = useDebounced(searchText)
  const [mediaDialog, setMediaDialog] = useState<AssetMediaState | null>(null)
  const [selectedEntityTypeId, setSelectedEntityTypeId] = useState<string | null>(null)
  const [hasUserSelectedCollection, setHasUserSelectedCollection] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const uploadInputRef = useRef<HTMLInputElement | null>(null)

  const supportedReferenceTypeIds = useMemo(() => {
    const explicit = getSupportedReferenceTypeIdsForAssetType(currentAssetType)
    if (explicit.length > 0) return explicit
    const all = new Set<string>()
    assetEntityTypes.forEach((config) => {
      config.allowedTypeIds.forEach((typeId) => all.add(typeId))
    })
    return Array.from(all)
  }, [assetEntityTypes, currentAssetType])

  const supportedReferenceTypeIdSet = useMemo(
    () => new Set(supportedReferenceTypeIds.map((id) => normalizeValue(id))),
    [supportedReferenceTypeIds]
  )

  const collectionCandidates = useMemo(() => {
    if (supportedReferenceTypeIds.length === 0) return assetEntityTypes
    const filtered = assetEntityTypes.filter((config) =>
      config.allowedTypeIds.some((typeId) => supportedReferenceTypeIdSet.has(normalizeValue(typeId)))
    )
    return filtered.length > 0 ? filtered : assetEntityTypes
  }, [assetEntityTypes, supportedReferenceTypeIdSet, supportedReferenceTypeIds.length])

  const assetTypesAdminEntityType = useMemo(
    () => findEntityTypeByName(entityTypes, ADMIN_ASSET_TYPES_NAME),
    [entityTypes]
  )
  const orderField = useMemo(
    () => assetTypesAdminEntityType?.fields.asJsReadonlyArrayView().find((field) => isOrderField(field)),
    [assetTypesAdminEntityType]
  )
  const assetTypesAdminEntriesByType = useEntitiesOfType(
    {
      typeIds: orderField && assetTypesAdminEntityType ? [assetTypesAdminEntityType.id] : [],
      allTypes: entityTypes,
      pageSize: 200,
    },
    [assetTypesAdminEntityType?.id.toString() ?? '', orderField?.id.toString() ?? '', entityTypes.size]
  )
  const assetTypesAdminEntries = assetTypesAdminEntityType
    ? assetTypesAdminEntriesByType?.get(assetTypesAdminEntityType.id)
    : undefined

  const collectionOrderMap = useMemo(() => {
    if (!orderField || !assetTypesAdminEntries || !assetTypesAdminEntityType) return null
    const nameField = findFieldByName(assetTypesAdminEntityType, 'Name')
    if (!nameField) return null
    const orderByName = new Map<string, number>()
    assetTypesAdminEntries.forEach((entry) => {
      const values = entry.entity.valuesByFieldId.asJsReadonlyMapView()
      const name = fieldValueToString(values.get(nameField.id) ?? null).trim()
      if (!name) return
      const orderValue = fieldValueToString(values.get(orderField.id) ?? null).trim()
      const order = Number.parseFloat(orderValue)
      if (!Number.isFinite(order)) return
      const key = normalizeValue(name)
      const existing = orderByName.get(key)
      if (existing === undefined || order < existing) {
        orderByName.set(key, order)
      }
    })
    return orderByName
  }, [assetTypesAdminEntries, assetTypesAdminEntityType, orderField])

  const orderedCollections = useMemo(() => {
    const base = [...collectionCandidates]
    if (!collectionOrderMap || collectionOrderMap.size === 0) return base
    const withOrder = base.map((config, index) => {
      const byDisplay = collectionOrderMap.get(normalizeValue(config.entityTypeName))
      const byFullName = collectionOrderMap.get(normalizeValue(config.entityType.name))
      const order = byDisplay ?? byFullName
      return { config, index, order }
    })
    return withOrder
      .sort((left, right) => {
        const orderLeft = left.order ?? Number.POSITIVE_INFINITY
        const orderRight = right.order ?? Number.POSITIVE_INFINITY
        if (orderLeft !== orderRight) return orderLeft - orderRight
        return left.index - right.index
      })
      .map(({ config }) => config)
  }, [collectionCandidates, collectionOrderMap])

  const fallbackConfig = orderedCollections[0] ?? assetEntityTypes[0]!
  const currentAssetConfig = useMemo(() => {
    if (!currentAsset) return undefined
    return assetEntityTypes.find((config) => config.entityTypeId.equals(currentAsset.entity.typeId))
  }, [assetEntityTypes, currentAsset])
  const currentLinkedAssetTypeId = useMemo(
    () => (currentAsset ? resolveAssetTypeForEntity(currentAsset, currentAssetConfig)?.id : undefined),
    [currentAsset, currentAssetConfig]
  )

  useEffect(() => {
    setSelectedEntityTypeId(null)
    setHasUserSelectedCollection(false)
  }, [assetReference.uuid])

  useEffect(() => {
    if (orderedCollections.length === 0) return
    const nextSelection = resolveNextCollectionEntityTypeId({
      orderedCollections,
      currentLinkedAssetTypeId,
      supportedReferenceTypeIds,
      selectedEntityTypeId,
      hasUserSelectedCollection,
    })
    if (nextSelection && nextSelection !== selectedEntityTypeId) {
      setSelectedEntityTypeId(nextSelection)
    }
  }, [
    currentLinkedAssetTypeId,
    hasUserSelectedCollection,
    orderedCollections,
    selectedEntityTypeId,
    supportedReferenceTypeIds,
  ])

  const activeConfig = useMemo(() => {
    if (orderedCollections.length === 0) return undefined
    if (!selectedEntityTypeId) return fallbackConfig
    return (
      orderedCollections.find((config) => config.entityTypeId.toString() === selectedEntityTypeId) ?? fallbackConfig
    )
  }, [fallbackConfig, orderedCollections, selectedEntityTypeId])

  const effectiveConfig = activeConfig ?? fallbackConfig
  const activeEntityType = effectiveConfig.entityType
  const activeTypeId = effectiveConfig.entityTypeId
  const entityRepository = useEntityRepository({ typeId: activeTypeId, types: entityTypes }, [activeTypeId.toString()])

  const uploadableTypes = useMemo(() => {
    const base = effectiveConfig.allowedTypes.filter((type) => SUPPORTED_UPLOAD_TYPE_IDS.has(type.id))
    if (supportedReferenceTypeIdSet.size === 0) return base
    return base.filter((type) => supportedReferenceTypeIdSet.has(normalizeValue(type.id)))
  }, [effectiveConfig, supportedReferenceTypeIdSet])
  const uploadAccept = useMemo(() => buildUploadAccept(uploadableTypes), [uploadableTypes])
  const uploadTypeLabels = useMemo(
    () => uploadableTypes.map((type) => type.displayName || type.id.toUpperCase()),
    [uploadableTypes]
  )

  const payloadField = useMemo(
    () => findFieldById(activeEntityType, effectiveConfig.payloadFieldId),
    [activeEntityType, effectiveConfig.payloadFieldId]
  )
  const typeField = useMemo(
    () => findFieldById(activeEntityType, effectiveConfig.typeFieldId),
    [activeEntityType, effectiveConfig.typeFieldId]
  )
  const nameField = useMemo(() => findFieldByName(activeEntityType, 'Name'), [activeEntityType])
  const mimeField = useMemo(
    () => findFieldByName(activeEntityType, 'Mime Type') ?? findFieldByName(activeEntityType, 'MIME Type'),
    [activeEntityType]
  )

  const typeFieldReferenceTypeIds = useMemo(() => {
    if (!typeField || typeField.type !== FieldType.Reference) return []
    return Array.from(typeField.validReferenceTypes?.asJsReadonlySetView() ?? [])
  }, [typeField])

  const typeReferenceEntitiesByTypeId = useEntitiesOfType(
    {
      typeIds: typeFieldReferenceTypeIds,
      allTypes: entityTypes,
      pageSize: 200,
    },
    [typeFieldReferenceTypeIds.map((id) => id.toString()).join('|'), entityTypes.size]
  )

  const activeFieldIds = useMemo(() => getAssetFieldIds(activeConfig), [activeConfig])
  const currentFieldIds = useMemo(
    () => getAssetFieldIds(currentAssetConfig ?? activeConfig),
    [currentAssetConfig, activeConfig]
  )

  const { entities, loadMore, hasMore, loading, reload } = usePaginatedEntitiesOfType({
    typeId: activeTypeId,
    searchQuery: debouncedSearchQuery,
    allTypes: entityTypes,
  })

  const visibleEntities = useMemo(
    () =>
      entities.filter((asset) => {
        const resolvedType = resolveAssetTypeForEntity(asset, activeConfig)
        if (!resolvedType) return false
        if (supportedReferenceTypeIdSet.size === 0) return true
        return supportedReferenceTypeIdSet.has(normalizeValue(resolvedType.id))
      }),
    [activeConfig, entities, supportedReferenceTypeIdSet]
  )

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!hasMore || loading) return
      const target = event.currentTarget
      const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 200
      if (nearBottom) loadMore()
    },
    [hasMore, loadMore, loading]
  )

  const trimmedAssetId = assetReference.uuid.trim()
  const hasAssetId = trimmedAssetId.length > 0
  const currentFallbackPrimary = !hasAssetId ? 'New asset' : currentAssetLoading ? 'Loading asset...' : 'Unknown asset'
  const currentFallbackSecondary = hasAssetId && !currentAssetLoading ? trimmedAssetId : undefined
  const openMediaDialog = useCallback((media: AssetMediaState) => {
    setMediaDialog(media)
  }, [])

  const resolveTypeReferenceId = useCallback(
    (assetType: AssetTypeDefinition) => {
      if (!typeField) {
        throw new Error('Type field is missing for this asset collection.')
      }
      if (typeField.type !== FieldType.Reference) return assetType.id

      if (typeFieldReferenceTypeIds.length === 0) {
        throw new Error('Type field is missing reference metadata.')
      }
      if (typeFieldReferenceTypeIds.length > 1) {
        throw new Error(
          `Type field allows multiple reference types (${typeFieldReferenceTypeIds
            .map((id) => id.toString())
            .join(', ')}).`
        )
      }

      const referenceTypeId = typeFieldReferenceTypeIds[0]
      const referencedEntities = typeReferenceEntitiesByTypeId?.get(referenceTypeId)
      if (!referencedEntities) {
        throw new Error('Asset type catalog is still loading. Please try again in a moment.')
      }

      const referenceType = entityTypes.get(referenceTypeId)
      const referenceNameField = referenceType ? findFieldByName(referenceType, 'Name') : undefined
      const normalizedTargets = new Set(
        [assetType.id, assetType.displayName]
          .map((value) => value?.trim().toLowerCase())
          .filter((value): value is string => !!value)
      )

      let matchId: string | null = null
      referencedEntities.forEach((entity) => {
        const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
        const label = entity.primaryLabelText?.trim() ?? ''
        const nameValue = referenceNameField ? fieldValueToString(values.get(referenceNameField.id) ?? null).trim() : ''
        const candidates = [label, nameValue].map((value) => value.trim().toLowerCase()).filter(Boolean)
        if (!candidates.some((candidate) => normalizedTargets.has(candidate))) return
        const id = entity.entity.id.toString()
        if (matchId && matchId !== id) {
          throw new Error(`Multiple asset type entries found for "${assetType.displayName ?? assetType.id}".`)
        }
        matchId = id
      })

      if (!matchId) {
        throw new Error(`Asset type "${assetType.displayName ?? assetType.id}" not found in this collection.`)
      }

      return matchId
    },
    [entityTypes, typeField, typeFieldReferenceTypeIds, typeReferenceEntitiesByTypeId]
  )

  const uploadAssetFile = useCallback(
    async (file: File) => {
      if (!payloadField) {
        throw new Error('Payload field is missing for this asset collection.')
      }
      if (payloadField.type !== FieldType.Text) {
        throw new Error(`Payload field "${payloadField.name}" must be a text field.`)
      }
      if (uploadableTypes.length === 0) {
        throw new Error('This asset collection does not support media uploads.')
      }

      const normalizedTypeId = normalizeUploadTypeId(file)
      if (!normalizedTypeId) {
        throw new Error(`Unsupported file type for "${file.name}". Allowed: ${uploadTypeLabels.join(', ') || 'none'}.`)
      }

      const assetType = uploadableTypes.find((type) => type.id === normalizedTypeId)
      if (!assetType) {
        throw new Error(
          `"${file.name}" is a ${normalizedTypeId.toUpperCase()} file, but this collection only allows ${uploadTypeLabels.join(
            ', '
          )}.`
        )
      }

      const payload = await readFileAsBase64(file)
      const draftValues: DraftFieldValue[] = [{ fieldId: payloadField.id, type: payloadField.type, value: payload }]

      if (nameField && nameField.type === FieldType.Text) {
        const baseName = getFileBaseName(file.name)
        if (baseName.length > 0) {
          draftValues.push({ fieldId: nameField.id, type: nameField.type, value: baseName })
        }
      }

      if (typeField) {
        const typeValue = typeField.type === FieldType.Reference ? resolveTypeReferenceId(assetType) : assetType.id
        draftValues.push({ fieldId: typeField.id, type: typeField.type, value: typeValue })
      }

      if (mimeField && mimeField.type === FieldType.Text) {
        const mimeValue = file.type || assetType.mimeType || ''
        if (mimeValue) {
          draftValues.push({ fieldId: mimeField.id, type: mimeField.type, value: mimeValue })
        }
      }

      const providedValues = new Map(draftValues.map((entry) => [entry.fieldId, entry.value]))
      const missingRequiredFields = activeEntityType.fields
        .asJsReadonlyArrayView()
        .filter((field) => field.required)
        .filter((field) => (providedValues.get(field.id) ?? '').trim().length === 0)
        .map((field) => field.name)

      if (missingRequiredFields.length > 0) {
        throw new Error(
          `This asset collection requires additional fields: ${missingRequiredFields.join(
            ', '
          )}. Please open the asset editor to fill them.`
        )
      }

      const entity = createEntityToCreate(activeTypeId, draftValues, [], new Map())
      await entityRepository.createEntity(entity)
    },
    [
      activeEntityType,
      activeTypeId,
      entityRepository,
      mimeField,
      nameField,
      payloadField,
      resolveTypeReferenceId,
      typeField,
      uploadTypeLabels,
      uploadableTypes,
    ]
  )

  const handleUploadFiles = useCallback(
    async (files: FileList | null) => {
      const selectedFiles = files ? Array.from(files) : []
      if (selectedFiles.length === 0) return

      setIsUploading(true)
      let successCount = 0
      const failures: string[] = []
      try {
        for (const file of selectedFiles) {
          try {
            await uploadAssetFile(file)
            successCount += 1
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error)
            failures.push(`${file.name}: ${message}`)
          }
        }
      } finally {
        setIsUploading(false)
      }

      if (successCount > 0) {
        reload()
        showSnackBar(`Uploaded ${successCount} file${successCount === 1 ? '' : 's'}.`, 'success')
      }
      if (failures.length > 0) {
        const summary = failures.length === 1 ? failures[0] : `${failures.length} uploads failed. ${failures[0]}`
        showSnackBar(summary, 'error')
      }
    },
    [reload, showSnackBar, uploadAssetFile]
  )

  const handleUploadInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      void handleUploadFiles(event.target.files)
      if (uploadInputRef.current) {
        uploadInputRef.current.value = ''
      }
    },
    [handleUploadFiles]
  )

  const handleUploadButtonClick = useCallback(() => {
    if (uploadInputRef.current && !isUploading) {
      uploadInputRef.current.click()
    }
  }, [isUploading])

  return (
    <>
      <Box sx={{ bgcolor: 'background.paper' }}>
        <Box sx={{ p: 2 }}>
          <AssetRow
            asset={currentAsset ?? undefined}
            assetConfig={currentAssetConfig ?? activeConfig}
            fieldIds={currentFieldIds}
            fallbackPrimary={currentFallbackPrimary}
            fallbackSecondary={currentFallbackSecondary}
            onShowMedia={openMediaDialog}
          />
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            {orderedCollections.length > 1 && (
              <TextField
                select
                label="Asset collection"
                value={selectedEntityTypeId ?? activeConfig?.entityTypeId.toString() ?? ''}
                onChange={(event) => {
                  setSelectedEntityTypeId(event.target.value)
                  setHasUserSelectedCollection(true)
                }}
                sx={{ minWidth: 220, flex: '0 0 220px' }}
              >
                {orderedCollections.map((config) => (
                  <MenuItem key={config.entityTypeId.toString()} value={config.entityTypeId.toString()}>
                    {config.entityTypeName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Search assets"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              fullWidth
            />
            {uploadableTypes.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={isUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  onClick={handleUploadButtonClick}
                  disabled={isUploading}
                  title="Upload media"
                >
                  Upload
                </Button>
                <input
                  ref={uploadInputRef}
                  type="file"
                  multiple
                  accept={uploadAccept || undefined}
                  style={{ display: 'none' }}
                  onChange={handleUploadInputChange}
                />
              </Box>
            )}
          </Stack>
        </Box>
        <Divider />
      </Box>
      <DialogContent sx={{ p: 0, flex: 1, minHeight: 0 }} onScroll={handleScroll}>
        <Stack spacing={0} divider={<Divider flexItem />} sx={{ px: 2, pb: 2 }}>
          {visibleEntities.map((asset) => (
            <AssetRow
              key={asset.entity.id.toString()}
              asset={asset}
              assetConfig={activeConfig}
              fieldIds={activeFieldIds}
              fallbackPrimary="Untitled asset"
              onDoubleClick={() => onSelectAsset(asset.entity.id.toString())}
              onShowMedia={openMediaDialog}
            />
          ))}
          {!loading && visibleEntities.length === 0 && (
            <Box sx={{ py: 2 }}>
              <Typography color="text.secondary">No assets found.</Typography>
            </Box>
          )}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <Dialog open={!!mediaDialog} onClose={() => setMediaDialog(null)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
          {mediaDialog &&
            (mediaDialog.mediaType === 'image' ? (
              <Box
                component="img"
                src={mediaDialog.src}
                alt={mediaDialog.label}
                sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            ) : mediaDialog.mediaType === 'audio' ? (
              <Box component="audio" controls src={mediaDialog.src} sx={{ width: '100%' }} />
            ) : (
              <Box
                component="video"
                controls
                src={mediaDialog.src}
                sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            ))}
        </DialogContent>
      </Dialog>
    </>
  )
}

export type AssetReferenceEditorDialogProps = {
  assetReference: AssetReferenceMatch
  assetEntityTypes: AssetEntityTypeConfig[]
  assetEntityTypesLoading: boolean
  currentAssetType?: AssetTypeDefinition
  onClose: () => void
  onSelectAsset: (assetId: string) => void
}

const AssetReferenceEditorDialog = ({
  assetReference,
  assetEntityTypes,
  assetEntityTypesLoading,
  currentAssetType,
  onClose,
  onSelectAsset,
}: AssetReferenceEditorDialogProps) => {
  const entityTypesRepo = useContext(EntityTypesContext)
  const entityTypes = entityTypesRepo.useAll()

  let content: ReactNode
  if (!entityTypes) {
    content = (
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={28} />
          <Typography color="text.secondary">Loading asset types...</Typography>
        </Stack>
      </DialogContent>
    )
  } else if (assetEntityTypesLoading) {
    content = (
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={28} />
          <Typography color="text.secondary">Loading asset collections...</Typography>
        </Stack>
      </DialogContent>
    )
  } else if (assetEntityTypes.length === 0) {
    content = (
      <DialogContent sx={{ p: 3 }}>
        <Typography color="error">No asset collections configured.</Typography>
      </DialogContent>
    )
  } else {
    content = (
      <AssetReferenceEditorDialogContent
        assetReference={assetReference}
        assetEntityTypes={assetEntityTypes}
        entityTypes={entityTypes}
        currentAssetType={currentAssetType}
        onSelectAsset={onSelectAsset}
      />
    )
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: { height: 'calc(100% - 64px)', maxHeight: 'calc(100% - 64px)', display: 'flex', flexDirection: 'column' },
      }}
    >
      {content}
    </Dialog>
  )
}

export default AssetReferenceEditorDialog
